import { Passkeys } from '@laravel/passkeys';

const showError = (scope, error) => {
    const target = scope.querySelector('[data-passkey-error]');

    if (!target) {
        return;
    }

    const message = error?.message || '';

    target.textContent = /\b429\b|too many/i.test(message)
        ? 'Too many attempts. Wait a minute, then try again.'
        : message || 'Something went wrong with your passkey. Please try again.';
    target.hidden = false;
};

const xsrfToken = () => {
    const cookie = document.cookie.split('; ').find((row) => row.startsWith('XSRF-TOKEN='));

    return cookie ? decodeURIComponent(cookie.split('=')[1]) : '';
};

const busy = (button, isBusy) => {
    button.disabled = isBusy;
    button.classList.toggle('opacity-60', isBusy);
};

const looksLikePasswordConfirmable = (message) => /confirm your password/i.test(message || '');

/**
 * Ask for the account password in a masked field and confirm it with the
 * server, so a recently-expired session can be renewed without a full sign-out.
 */
const confirmPassword = () => new Promise((resolve) => {
    const dialog = document.createElement('dialog');
    dialog.className = 'w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-lg backdrop:bg-black/40';
    dialog.innerHTML = `
        <form method="dialog" class="flex flex-col gap-4">
            <div>
                <h2 class="font-display text-base font-bold text-heading">Confirm your password</h2>
                <p class="mt-1 text-sm text-muted">For your security, please confirm your password to continue.</p>
            </div>
            <div>
                <input type="password" name="password" autocomplete="current-password" required
                    class="w-full rounded-xl border border-border bg-card px-4 py-2.5 text-sm text-heading outline-none focus:border-primary" />
                <p data-confirm-error hidden class="mt-2 text-xs font-medium text-accent-dark" role="alert"></p>
            </div>
            <div class="flex justify-end gap-2">
                <button type="button" data-confirm-cancel class="rounded-xl px-4 py-2 text-sm font-semibold text-muted hover:bg-surface">Cancel</button>
                <button type="submit" class="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark">Confirm</button>
            </div>
        </form>
    `;
    document.body.append(dialog);

    const form = dialog.querySelector('form');
    const input = dialog.querySelector('input[name="password"]');
    const submitButton = form.querySelector('button[type="submit"]');
    const errorTarget = dialog.querySelector('[data-confirm-error]');

    const finish = (result) => {
        dialog.close();
        dialog.remove();
        resolve(result);
    };

    dialog.querySelector('[data-confirm-cancel]').addEventListener('click', () => finish(false));
    dialog.addEventListener('cancel', () => finish(false));

    form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const password = input.value;
        submitButton.disabled = true;

        try {
            const response = await fetch('/password/confirm', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-XSRF-TOKEN': xsrfToken(),
                },
                body: JSON.stringify({ password }),
            });

            if (response.ok) {
                finish(true);

                return;
            }

            const body = await response.json().catch(() => ({}));
            errorTarget.textContent = body.errors?.password?.[0] ?? body.message ?? 'That password was not correct.';
            errorTarget.hidden = false;
        } catch {
            errorTarget.textContent = 'Something went wrong. Please try again.';
            errorTarget.hidden = false;
        }

        submitButton.disabled = false;
    });

    dialog.showModal();
    input.focus();
});

document.querySelectorAll('[data-passkey-login]').forEach((button) => {
    if (!Passkeys.isSupported()) {
        button.hidden = true;

        return;
    }

    button.hidden = false;

    button.addEventListener('click', async () => {
        const scope = button.closest('[data-passkey-scope]') ?? document;
        const remember = scope.querySelector('[name="remember"]')?.checked ?? false;

        busy(button, true);

        try {
            const response = await Passkeys.verify({ remember });
            window.location.href = response?.redirect ?? '/';
        } catch (error) {
            busy(button, false);
            showError(scope, error);
        }
    });
});

document.querySelectorAll('[data-passkey-register]').forEach((button) => {
    const scope = button.closest('[data-passkey-scope]') ?? document;

    if (!Passkeys.isSupported()) {
        button.disabled = true;
        showError(scope, { message: 'This browser does not support passkeys.' });

        return;
    }

    button.addEventListener('click', async () => {
        const name = scope.querySelector('[data-passkey-name]')?.value.trim();

        if (!name) {
            showError(scope, { message: 'Give your passkey a name first, for example "MacBook".' });

            return;
        }

        busy(button, true);

        try {
            try {
                await Passkeys.register({ name });
            } catch (error) {
                if (!looksLikePasswordConfirmable(error?.message) || !(await confirmPassword())) {
                    throw error;
                }

                await Passkeys.register({ name });
            }

            window.location.reload();
        } catch (error) {
            busy(button, false);
            showError(scope, error);
        }
    });
});

document.querySelectorAll('[data-passkey-delete]').forEach((button) => {
    button.addEventListener('click', async () => {
        const scope = button.closest('[data-passkey-scope]') ?? document;

        if (!window.confirm('Remove this passkey?')) {
            return;
        }

        busy(button, true);

        const attemptDelete = async () => {
            const response = await fetch(button.dataset.passkeyDelete, {
                method: 'DELETE',
                credentials: 'same-origin',
                headers: { Accept: 'application/json', 'X-XSRF-TOKEN': xsrfToken() },
            });

            if (response.ok) {
                return true;
            }

            const body = await response.json().catch(() => ({}));

            if (body.confirm_password && (await confirmPassword())) {
                return attemptDelete();
            }

            throw new Error(body.message ?? 'The passkey could not be removed.');
        };

        try {
            await attemptDelete();
            window.location.reload();
        } catch (error) {
            busy(button, false);
            showError(scope, error);
        }
    });
});
